import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Zap, ZapOff, RefreshCw, MessageSquare, ShoppingBag, TrendingUp, Users, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// State label dictionary matches sales_bot.py
const STATE_LABELS = {
  start: 'התחלה',
  pick_product: 'בוחר מוצר',
  confirm: 'מאשר מוצר',
  name: 'ממלא שם',
  address: 'ממלא כתובת',
  payment: 'בוחר תשלום',
  final: 'אישור סופי',
  done: 'הושלם',
};

const STATE_COLORS = {
  start: 'bg-stone-100 text-stone-700',
  pick_product: 'bg-amber-100 text-amber-800',
  confirm: 'bg-amber-100 text-amber-800',
  name: 'bg-blue-100 text-blue-800',
  address: 'bg-blue-100 text-blue-800',
  payment: 'bg-indigo-100 text-indigo-800',
  final: 'bg-purple-100 text-purple-800',
  done: 'bg-emerald-100 text-emerald-800',
};

const SalesBotPanel = () => {
  const [enabled, setEnabled] = useState(true);
  const [stats, setStats] = useState({ today: 0, this_week: 0, total_orders: 0, revenue_total: 0, active_conversations: 0 });
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgR, statsR, convR] = await Promise.all([
        axios.get(`${API}/wabot/sales-bot/config`),
        axios.get(`${API}/wabot/sales-bot/stats`),
        axios.get(`${API}/wabot/sales-bot/conversations?limit=30`),
      ]);
      setEnabled(cfgR.data?.enabled !== false);
      setStats(statsR.data || {});
      setConversations(convR.data?.conversations || []);
    } catch (e) {
      console.error('SalesBot fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const toggle = async () => {
    setToggling(true);
    try {
      const r = await axios.put(`${API}/wabot/sales-bot/config`, { enabled: !enabled });
      setEnabled(r.data?.enabled !== false);
      toast.success(r.data?.enabled ? 'בוט מכירות הופעל' : 'בוט מכירות הושבת');
    } catch (_e) {
      toast.error('שגיאה בעדכון');
    } finally {
      setToggling(false);
    }
  };

  const resetConversation = async (conv) => {
    if (!window.confirm(`לאפס את השיחה עם ${conv.from_jid}? הלקוח יחזור להתחלה.`)) return;
    try {
      await axios.delete(`${API}/wabot/sales-bot/conversations/${conv.from_jid}?session_id=${conv.session_id}`);
      toast.success('השיחה אופסה');
      fetchAll();
    } catch (_e) {
      toast.error('שגיאה');
    }
  };

  const fmtPhone = (jid) => {
    if (!jid) return '';
    const digits = jid.split('@')[0].replace(/\D/g, '');
    return digits.startsWith('972') ? '0' + digits.slice(3) : digits;
  };

  return (
    <div className="mt-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-5" dir="rtl" data-testid="sales-bot-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${enabled ? 'bg-emerald-500' : 'bg-stone-400'}`}>
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-black text-stone-800 flex items-center gap-2">
              בוט מכירות AI
              {enabled ? (
                <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold">פעיל</span>
              ) : (
                <span className="text-[10px] bg-stone-400 text-white px-2 py-0.5 rounded-full font-bold">כבוי</span>
              )}
            </h3>
            <p className="text-xs text-stone-500">לקוחות מזמינים דרך וואטסאפ — הזמנות נכנסות אוטומטית לדשבורד</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="p-2 bg-white hover:bg-stone-50 border border-stone-200 rounded-lg text-stone-600"
            title="רענן"
            data-testid="sales-bot-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={toggle}
            disabled={toggling}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-colors ${enabled ? 'bg-stone-700 hover:bg-stone-800 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} ${toggling ? 'opacity-60' : ''}`}
            data-testid="sales-bot-toggle"
          >
            {enabled ? <ZapOff className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
            {enabled ? 'השבת' : 'הפעל'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <StatCard icon={<ShoppingBag className="w-4 h-4" />} label="היום" value={stats.today} color="emerald" />
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="השבוע" value={stats.this_week} color="blue" />
        <StatCard icon={<MessageSquare className="w-4 h-4" />} label="סה״כ הזמנות" value={stats.total_orders} color="indigo" />
        <StatCard icon={<span className="text-xs font-black">₪</span>} label="הכנסה" value={`${Number(stats.revenue_total || 0).toLocaleString()} ₪`} color="amber" />
        <StatCard icon={<Users className="w-4 h-4" />} label="שיחות פעילות" value={stats.active_conversations} color="purple" />
      </div>

      {/* Active conversations */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="bg-stone-50 px-4 py-2.5 border-b border-stone-200 flex items-center justify-between">
          <h4 className="text-sm font-black text-stone-700">שיחות אחרונות עם הבוט</h4>
          <span className="text-[10px] text-stone-500">{conversations.length} שיחות</span>
        </div>
        {conversations.length === 0 ? (
          <p className="text-center text-stone-400 py-8 text-sm">אין שיחות עדיין</p>
        ) : (
          <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto">
            {conversations.map(c => {
              const state = c.state || 'start';
              return (
                <div key={`${c.session_id}-${c.from_jid}`} className="px-4 py-2 flex items-center justify-between gap-2 hover:bg-stone-50" data-testid={`sales-conv-${c.from_jid}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm text-stone-800" dir="ltr">{fmtPhone(c.from_jid)}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${STATE_COLORS[state] || 'bg-stone-100 text-stone-700'}`}>
                        {STATE_LABELS[state] || state}
                      </span>
                    </div>
                    {c.draft?.product_name && (
                      <p className="text-[11px] text-stone-500 mt-0.5 truncate">📦 {c.draft.product_name}</p>
                    )}
                    {c.updated_at && (
                      <p className="text-[10px] text-stone-400 mt-0.5" dir="ltr">{new Date(c.updated_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                    )}
                  </div>
                  {state !== 'done' && state !== 'start' && (
                    <button
                      type="button"
                      onClick={() => resetConversation(c)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                      title="אפס שיחה"
                      data-testid={`sales-conv-reset-${c.from_jid}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Flow preview */}
      <details className="mt-3 bg-white rounded-xl border border-stone-200 overflow-hidden">
        <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700 hover:bg-stone-50">
          איך הבוט עובד? (לחץ להרחבה)
        </summary>
        <div className="px-4 py-3 text-xs text-stone-600 space-y-1.5 border-t border-stone-200">
          <p><strong>1.</strong> לקוח שולח הודעה לוואטסאפ של העסק (לדוגמה: "אני רוצה ספריי השהיה")</p>
          <p><strong>2.</strong> הבוט מזהה את המוצר ושולח מחיר + שואל "להזמין?"</p>
          <p><strong>3.</strong> אם הלקוח אישר — הבוט מבקש: שם → כתובת → אופן תשלום (מזומן/ביט)</p>
          <p><strong>4.</strong> הבוט מציג סיכום ומבקש אישור סופי</p>
          <p><strong>5.</strong> ההזמנה נכנסת לדשבורד הראשי עם סטטוס "בטיפול" וסימן `WhatsApp Bot`</p>
          <p className="text-stone-400 mt-2 text-[10px]">בכל שלב הלקוח יכול לכתוב "ביטול" כדי להתחיל מחדש.</p>
        </div>
      </details>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const palette = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  return (
    <div className={`rounded-xl border p-2.5 ${palette[color] || palette.emerald}`}>
      <div className="flex items-center gap-1.5 mb-0.5 opacity-70">
        {icon}
        <span className="text-[10px] font-bold">{label}</span>
      </div>
      <p className="text-base font-black truncate">{value}</p>
    </div>
  );
};

export default SalesBotPanel;
